import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useImpersonation } from '../contexts/impersonation/ImpersonationContext';
import { apiFetch, ApiFetchOptions } from './apiFetch';

export type ApiFetchFn = (
  path: string,
  options?: Omit<ApiFetchOptions, 'token' | 'impersonatedClientID'>,
) => Promise<Response>;

/**
 * React hook returning an apiFetch function bound to the current auth token
 * and the active impersonation target (if any).
 *
 * When impersonation is active, every request made through the returned
 * function automatically carries X-Impersonate-Client-ID. The /impersonation/*
 * paths are exempt and never carry the header (enforced inside apiFetch).
 *
 * Use this from React component bodies and custom hooks. For non-React
 * contexts, import apiFetch directly and pass token + impersonatedClientID
 * explicitly.
 */
export function useApiFetch(): ApiFetchFn {
  const auth = useAuth();
  const { target } = useImpersonation();
  const token = auth.user?.access_token ?? null;
  const impersonatedClientID = target?.id ?? null;

  return useCallback(
    (path, options = {}) =>
      apiFetch(path, {
        ...options,
        token,
        impersonatedClientID,
      }),
    [token, impersonatedClientID],
  );
}
